'use client';

import { useEffect, useMemo, useState } from 'react';
import { useBraintree, type HostedFieldName } from './use-braintree';
import { CardBrandIcon } from './icons';

// Container IDs for Braintree Hosted Fields
const CONTAINER_IDS = {
  number: 'braintree-card-number',
  expirationDate: 'braintree-expiration-date',
  cvv: 'braintree-cvv',
} as const;

export interface CreditCardFormRef {
  tokenize: () => Promise<{ nonce: string }>;
  canTokenize: boolean;
  isReady: boolean;
  isLoading: boolean;
  error: string | null;
}

interface CreditCardFormProps {
  onReady?: () => void;
  onError?: (error: string) => void;
  onCardTypeChange?: (cardType: string | null) => void;
  onValidityChange?: (canTokenize: boolean) => void;
  disabled?: boolean;
  formRef?: React.RefObject<CreditCardFormRef | null>;
}

export default function CreditCardForm({
  onReady,
  onError,
  onCardTypeChange,
  onValidityChange,
  disabled = false,
  formRef,
}: CreditCardFormProps) {
  const [touchedFields, setTouchedFields] = useState<Record<HostedFieldName, boolean>>({
    number: false,
    expirationDate: false,
    cvv: false,
  });

  const braintree = useBraintree({
    containerIds: CONTAINER_IDS,
    onReady,
    onError: (error) => onError?.(error.userMessage),
    onCardTypeChange,
    onFocusChange: (fieldName, isFocused) => {
      // Mark field as touched when it loses focus
      if (!isFocused) {
        setTouchedFields((prev) => ({ ...prev, [fieldName]: true }));
      }
    },
  });

  // Expose ref for parent components
  if (formRef) {
    formRef.current = {
      tokenize: braintree.tokenize,
      canTokenize: braintree.canTokenize,
      isReady: braintree.isReady,
      isLoading: braintree.isLoading,
      error: braintree.error?.userMessage || null,
    };
  }

  // Notify parent when form validity changes
  useEffect(() => {
    onValidityChange?.(braintree.canTokenize);
  }, [braintree.canTokenize, onValidityChange]);

  // Get validation classes for a field container
  const getFieldClasses = (fieldName: HostedFieldName) => {
    // Use explicit height to match standard form inputs
    // The Braintree iframe needs a fixed height container
    const baseClasses = 'form-input block w-full h-[46px] transition-colors duration-200';

    if (!braintree.isReady) return baseClasses;

    const state = braintree.fieldStates[fieldName];
    const isTouched = touchedFields[fieldName];
    const isFocused = state.isFocused;

    // Focus state
    if (isFocused) {
      return `${baseClasses} ring-2 ring-blue-300 border-blue-300`;
    }

    // Validation states (only show after field has been touched)
    if (isTouched && !state.isEmpty) {
      if (state.isValid) {
        return `${baseClasses} border-emerald-500`;
      }
      if (!state.isPotentiallyValid) {
        return `${baseClasses} border-red-500`;
      }
    }

    return baseClasses;
  };

  // Get field-level error message
  const getFieldError = (fieldName: HostedFieldName): string | null => {
    if (!braintree.isReady) return null;

    const state = braintree.fieldStates[fieldName];
    const isTouched = touchedFields[fieldName];

    if (isTouched && !state.isEmpty && !state.isPotentiallyValid) {
      switch (fieldName) {
        case 'number':
          return 'Invalid card number';
        case 'expirationDate':
          return 'Invalid date';
        case 'cvv':
          return 'Invalid CVV';
        default:
          return 'Invalid';
      }
    }

    return null;
  };

  // Loading skeleton
  const skeleton = useMemo(
    () => (
      <div className="space-y-4 animate-pulse">
        <div>
          <div className="h-4 w-24 bg-slate-200 rounded mb-1" />
          <div className="h-[46px] bg-slate-100 rounded-sm" />
        </div>
        <div className="grid grid-cols-2 gap-3 max-w-[280px]">
          <div>
            <div className="h-4 w-12 bg-slate-200 rounded mb-1" />
            <div className="h-[46px] bg-slate-100 rounded-sm" />
          </div>
          <div>
            <div className="h-4 w-8 bg-slate-200 rounded mb-1" />
            <div className="h-[46px] bg-slate-100 rounded-sm" />
          </div>
        </div>
      </div>
    ),
    [],
  );

  // Error state with retry
  if (braintree.status === 'error' && braintree.error && !braintree.error.recoverable) {
    return (
      <div className="text-center py-8">
        <p className="text-red-500 mb-4">{braintree.error.userMessage}</p>
        <button onClick={braintree.retry} className="btn bg-blue-500 text-white hover:bg-blue-600">
          Try Again
        </button>
      </div>
    );
  }

  const cardNumberError = getFieldError('number');
  const expirationError = getFieldError('expirationDate');
  const cvvError = getFieldError('cvv');

  return (
    <div className={disabled ? 'opacity-50 pointer-events-none' : ''}>
      {/* Show skeleton while loading */}
      {!braintree.isReady && (
        <div className="mb-4">
          {skeleton}
          <p className="text-sm text-slate-500 text-center mt-4">Loading secure payment form...</p>
        </div>
      )}

      {/* Actual form - opacity 0 until ready to prevent layout shift */}
      <div
        className={`space-y-4 transition-opacity duration-200 ${
          braintree.isReady ? 'opacity-100' : 'opacity-0 h-0 overflow-hidden'
        }`}
      >
        {/* Card Number */}
        <div className="form-control">
          <label className="block mb-1 text-sm font-medium text-slate-600">
            <span>Card number</span>
            {cardNumberError && (
              <span className="form-field-error float-right">{cardNumberError}</span>
            )}
          </label>
          <div className="relative">
            <div id={CONTAINER_IDS.number} className={`${getFieldClasses('number')} pr-14`} />
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <CardBrandIcon cardType={braintree.cardType} className="w-10 h-7" bordered />
            </div>
          </div>
        </div>

        {/* Expiration and CVV - side by side, narrow */}
        <div className="grid grid-cols-2 gap-3 max-w-[280px]">
          <div className="form-control">
            <label className="block mb-1 text-sm font-medium text-slate-600">
              <span>Expiry</span>
              {expirationError && (
                <span className="form-field-error float-right">{expirationError}</span>
              )}
            </label>
            <div id={CONTAINER_IDS.expirationDate} className={getFieldClasses('expirationDate')} />
          </div>

          <div className="form-control">
            <label className="block mb-1 text-sm font-medium text-slate-600">
              <span>CVV</span>
              {cvvError && <span className="form-field-error float-right">{cvvError}</span>}
            </label>
            <div id={CONTAINER_IDS.cvv} className={getFieldClasses('cvv')} />
          </div>
        </div>
      </div>

      {/* Form-level error display */}
      {braintree.error && braintree.error.recoverable && (
        <div className="form-error mt-4">{braintree.error.userMessage}</div>
      )}
    </div>
  );
}
